import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const TenantConfiguration = ({ company }) => {
  const [awsConfig, setAwsConfig] = useState(null);
  const [llmConfig, setLlmConfig] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // AWS Form State
  const [awsForm, setAwsForm] = useState({
    access_key: '',
    secret_key: '',
    bucket_name: '',
    region: 'us-east-1'
  });
  const [savingAws, setSavingAws] = useState(false);
  
  // LLM Form State
  const [llmForm, setLlmForm] = useState({
    openai_key: '',
    anthropic_key: ''
  });
  const [savingLlm, setSavingLlm] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('[TenantConfig] Component mounted with company:', company);
    if (company) {
      loadConfiguration();
    } else {
      console.log('[TenantConfig] No company provided');
      setLoading(false);
      setError('No se proporcionó información de la empresa');
    }
  }, [company]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[TenantConfig] Loading for company:', company.id, company.name);
      
      // Load AWS config
      console.log('[TenantConfig] Fetching AWS config...');
      const awsResponse = await axios.get(`${API}/api/companies/${company.id}/aws-config`);
      console.log('[TenantConfig] AWS config loaded:', awsResponse.data);
      setAwsConfig(awsResponse.data);
      
      // Load LLM config
      console.log('[TenantConfig] Fetching LLM config...');
      const llmResponse = await axios.get(`${API}/api/companies/${company.id}/llm-config`);
      console.log('[TenantConfig] LLM config loaded:', llmResponse.data);
      setLlmConfig(llmResponse.data);
      
      // Load usage stats
      console.log('[TenantConfig] Fetching usage stats...');
      const statsResponse = await axios.get(`${API}/api/companies/${company.id}/usage-stats`);
      console.log('[TenantConfig] Stats loaded:', statsResponse.data);
      setUsageStats(statsResponse.data);
      
    } catch (error) {
      console.error('[TenantConfig] Error loading configuration:', error);
      console.error('[TenantConfig] Error details:', {
        response: error.response,
        message: error.message,
        status: error.response?.status
      });
      
      const errorMsg = error.response?.data?.detail || error.message || 'Error al cargar configuración';
      console.error('[TenantConfig] Setting error:', errorMsg);
      setError(errorMsg);
      toast.error('Error al cargar configuración del tenant');
    } finally {
      console.log('[TenantConfig] Loading complete, setting loading to false');
      setLoading(false);
    }
  };

  const handleSaveAws = async () => {
    // Validation
    if (!awsForm.access_key || !awsForm.secret_key || !awsForm.bucket_name) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    try {
      setSavingAws(true);
      
      await axios.post(`${API}/api/companies/${company.id}/aws-config`, awsForm);
      
      toast.success('Credenciales AWS configuradas exitosamente');
      
      // Clear form
      setAwsForm({
        access_key: '',
        secret_key: '',
        bucket_name: '',
        region: 'us-east-1'
      });
      
      // Reload config
      await loadConfiguration();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al configurar AWS');
    } finally {
      setSavingAws(false);
    }
  };

  const handleSaveLlm = async () => {
    // At least one key required
    if (!llmForm.openai_key && !llmForm.anthropic_key) {
      toast.error('Debes proporcionar al menos una key (OpenAI o Anthropic)');
      return;
    }

    try {
      setSavingLlm(true);
      
      await axios.post(`${API}/api/companies/${company.id}/llm-config`, llmForm);
      
      toast.success('Keys LLM configuradas exitosamente');
      
      // Clear form
      setLlmForm({
        openai_key: '',
        anthropic_key: ''
      });
      
      // Reload config
      await loadConfiguration();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al configurar LLM keys');
    } finally {
      setSavingLlm(false);
    }
  };

  const handleDeleteAws = async () => {
    if (!window.confirm('¿Estás seguro de eliminar la configuración AWS?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/companies/${company.id}/aws-config`);
      toast.success('Configuración AWS eliminada');
      await loadConfiguration();
    } catch (error) {
      toast.error('Error al eliminar configuración AWS');
    }
  };

  const handleDeleteLlm = async () => {
    if (!window.confirm('¿Estás seguro de eliminar las keys LLM?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/companies/${company.id}/llm-config`);
      toast.success('Keys LLM eliminadas');
      await loadConfiguration();
    } catch (error) {
      toast.error('Error al eliminar keys LLM');
    }
  };

  // Render component even if there's no company (for debugging)
  if (!company) {
    return (
      <div className="p-4">
        <p className="text-red-400">Error: No se proporcionó información de la empresa</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4">
        <p className="text-white">Cargando configuración para {company.name}...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 space-y-4">
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
          <h3 className="text-red-400 font-semibold mb-2">Error al cargar configuración</h3>
          <p className="text-red-300 text-sm">{error}</p>
          <p className="text-slate-400 text-sm mt-2">
            Nota: Esta funcionalidad requiere que el backend tenga los endpoints de configuración de tenant implementados.
          </p>
        </div>
        <Button onClick={loadConfiguration} variant="outline">
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Configuración de Tenant</h2>
        <p className="text-slate-400">
          {company?.name || 'Sin nombre'}
        </p>
      </div>

      <Tabs defaultValue="aws" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="aws">AWS S3</TabsTrigger>
          <TabsTrigger value="llm">LLM Keys</TabsTrigger>
          <TabsTrigger value="stats">Estadísticas</TabsTrigger>
        </TabsList>

        {/* AWS Configuration */}
        <TabsContent value="aws">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Configuración S3</h3>
                {awsConfig?.configured ? (
                  <div className="space-y-3">
                    <Badge variant="success" className="mb-2">✓ Configurado</Badge>
                    <div className="bg-slate-900 p-4 rounded-lg space-y-2">
                      <p><strong>Bucket:</strong> {awsConfig.bucket_name}</p>
                      <p><strong>Region:</strong> {awsConfig.region}</p>
                      <p><strong>Configurado:</strong> {new Date(awsConfig.configured_at).toLocaleString()}</p>
                    </div>
                    <Button onClick={handleDeleteAws} variant="destructive">
                      Eliminar Configuración
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Badge variant="secondary">No configurado</Badge>
                    
                    <div className="space-y-3 mt-4">
                      <div>
                        <Label>Access Key ID</Label>
                        <Input
                          type="text"
                          value={awsForm.access_key}
                          onChange={(e) => setAwsForm({...awsForm, access_key: e.target.value})}
                          placeholder="AKIA..."
                        />
                      </div>
                      
                      <div>
                        <Label>Secret Access Key</Label>
                        <Input
                          type="password"
                          value={awsForm.secret_key}
                          onChange={(e) => setAwsForm({...awsForm, secret_key: e.target.value})}
                          placeholder="••••••••"
                        />
                      </div>
                      
                      <div>
                        <Label>Bucket Name</Label>
                        <Input
                          type="text"
                          value={awsForm.bucket_name}
                          onChange={(e) => setAwsForm({...awsForm, bucket_name: e.target.value})}
                          placeholder="my-tenant-bucket"
                        />
                      </div>
                      
                      <div>
                        <Label>Region</Label>
                        <select
                          value={awsForm.region}
                          onChange={(e) => setAwsForm({...awsForm, region: e.target.value})}
                          className="w-full p-2 bg-slate-800 border border-slate-700 rounded-md"
                        >
                          <option value="us-east-1">US East (N. Virginia)</option>
                          <option value="us-west-2">US West (Oregon)</option>
                          <option value="eu-west-1">EU (Ireland)</option>
                          <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                        </select>
                      </div>
                      
                      <Button onClick={handleSaveAws} disabled={savingAws}>
                        {savingAws ? 'Guardando...' : 'Guardar Configuración'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* LLM Configuration */}
        <TabsContent value="llm">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Keys LLM</h3>
                {llmConfig?.configured ? (
                  <div className="space-y-3">
                    <Badge variant="success" className="mb-2">✓ Configurado</Badge>
                    <div className="bg-slate-900 p-4 rounded-lg space-y-2">
                      <p>
                        <strong>OpenAI:</strong>{' '}
                        {llmConfig.openai_configured ? (
                          <Badge variant="success">✓ Configurado</Badge>
                        ) : (
                          <Badge variant="secondary">No configurado</Badge>
                        )}
                      </p>
                      <p>
                        <strong>Anthropic:</strong>{' '}
                        {llmConfig.anthropic_configured ? (
                          <Badge variant="success">✓ Configurado</Badge>
                        ) : (
                          <Badge variant="secondary">No configurado</Badge>
                        )}
                      </p>
                      <p><strong>Configurado:</strong> {new Date(llmConfig.configured_at).toLocaleString()}</p>
                    </div>
                    <Button onClick={handleDeleteLlm} variant="destructive">
                      Eliminar Keys
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Badge variant="secondary">No configurado</Badge>
                    
                    <div className="space-y-3 mt-4">
                      <div>
                        <Label>OpenAI API Key (Opcional)</Label>
                        <Input
                          type="password"
                          value={llmForm.openai_key}
                          onChange={(e) => setLlmForm({...llmForm, openai_key: e.target.value})}
                          placeholder="sk-..."
                        />
                      </div>
                      
                      <div>
                        <Label>Anthropic API Key (Opcional)</Label>
                        <Input
                          type="password"
                          value={llmForm.anthropic_key}
                          onChange={(e) => setLlmForm({...llmForm, anthropic_key: e.target.value})}
                          placeholder="sk-ant-..."
                        />
                      </div>
                      
                      <p className="text-sm text-slate-400">
                        * Al menos una key es requerida
                      </p>
                      
                      <Button onClick={handleSaveLlm} disabled={savingLlm}>
                        {savingLlm ? 'Guardando...' : 'Guardar Keys'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Usage Stats */}
        <TabsContent value="stats">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Estadísticas de Uso</h3>
            {usageStats ? (
              <div className="space-y-4">
                <div className="bg-slate-900 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Uso de LLM</h4>
                  <p>Llamadas totales: {usageStats.llm_usage?.total_calls || 0}</p>
                  <p>Tokens usados: {usageStats.llm_usage?.total_tokens || 0}</p>
                </div>
                
                <div className="bg-slate-900 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Almacenamiento</h4>
                  <p>Archivos subidos: {usageStats.storage?.files_uploaded || 0}</p>
                  {usageStats.storage?.s3_storage && (
                    <>
                      <p>Archivos en S3: {usageStats.storage.s3_storage.total_files || 0}</p>
                      <p>Tamaño total: {usageStats.storage.s3_storage.total_size_mb || 0} MB</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <p>No hay estadísticas disponibles</p>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TenantConfiguration;
